import React from 'react';

import { twMerge } from 'tailwind-merge';

import type { Dayjs } from 'dayjs';

import dayjs from 'dayjs';

import { useField } from 'formik';

import { createLocale } from 'locales';

import type { FreelancerAvailableTime } from 'state/remote/types';

import Typography from 'components/typography/typography';
import Checkbox from 'components/checkbox/checkbox';
import TimeRangeInput from 'components/time-range-input/time-range-input';

interface AvailableDayItemProps {
  index: number;
}

const globalLocalize = createLocale('global');

const AvailableDayItem: React.FC<AvailableDayItemProps> = (props) => {
  const { index } = props;
  const [field, meta, { setValue }] = useField('availability');
  const { available, date, end, start } = field.value?.[index];
  const error = meta.error?.[index] as unknown as FreelancerAvailableTime;

  const switchAvailabilityStatus = React.useCallback(() => {
    const updatedList = field.value.map(
      (item: FreelancerAvailableTime, _index: number) => ({
        ...item,
        available: _index === index ? !item.available : item.available,
        end: _index === index && item.available ? null : item.end,
        start: _index === index && item.available ? null : item.start,
      }),
    );
    setValue(updatedList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value, index]);

  const onChange = React.useCallback(
    (values: [Dayjs?, Dayjs?]) => {
      const updatedList = field.value.map(
        (item: FreelancerAvailableTime, _index: number) => {
          return {
            ...item,
            end:
              _index === index ? values[1]?.format('HH:mm') ?? null : item.end,
            start:
              _index === index
                ? values[0]?.format('HH:mm') ?? null
                : item.start,
          };
        },
      );
      setValue(updatedList);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [field.value, index],
  );

  return (
    <div className="flex justify-center gap-x-10 md:justify-between self-center items-center w-full flex-wrap gap-y-3">
      <div className="flex flex-col w-24 md:self-center">
        <Typography as="p" variant="body-large-bold">
          {dayjs(date, 'YYYY-MM-DD').format('dddd')}
        </Typography>
        <Typography as="p" variant="body-small">
          {date}
        </Typography>
      </div>
      <div className="flex flex-col gap-y-1">
        <TimeRangeInput
          disabled={!available}
          defaultValue={[
            start ? dayjs(start, 'HH:mm') : undefined,
            end ? dayjs(end, 'HH:mm') : undefined,
          ]}
          hasError={!!error}
          onChange={onChange}
          minuteStep={15}
          className="flex-wrap gap-y-1 justify-center md:justify-start"
        />
        {error && (error.start || error.end) && (
          <Typography
            as="p"
            variant="body-medium"
            className={twMerge('text-destructive')}
          >
            {error.start ?? error.end}
          </Typography>
        )}
      </div>
      <div className="flex items-center gap-x-2">
        <Checkbox
          variant={available ? 'checked' : 'unchecked'}
          onClick={switchAvailabilityStatus}
        />
        <Typography as="p" variant="body-large" className="text-purple">
          {globalLocalize('available')}
        </Typography>
      </div>
    </div>
  );
};

export default AvailableDayItem;
